// ui/equipmentTooltip.js
// Shared equipment tooltip utilities for crafting skill UIs

import { gameState } from '../game/gameState.js';
import { getItem, getItemName, getItemIcon } from '../data/items/index.js';
import { SET_BONUSES, getActiveBonuses } from '../data/items/setBonusData.js';
import { SET_INFO } from '../data/items/setArmor.js';
import { getTalentFlatBonus } from '../systems/talentTree/talentTree.js';
import { SKILLS } from '../game/constants.js';

// Tier colors for equipment UI styling (border glow, name color)
// These are for tier indication, NOT material color matching
const TIER_COLORS = {
    tier1: '#CD853F',   // Tier 1 - peru/tan
    tier2: '#3CB371',   // Tier 2 - medium sea green
    tier3: '#5B9BD5',   // Tier 3 - royal blue
    tier4: '#E85D75',   // Tier 4 - crimson
    tier5: '#A8A8A8',   // Tier 5 - silver
    tier6: '#B088F9'    // Tier 6 - purple
};

/**
 * Get tier color based on combat level requirement
 */
export function getTierColor(combatLevel) {
    if (!combatLevel || combatLevel <= 1) return TIER_COLORS.tier1;
    if (combatLevel <= 15) return TIER_COLORS.tier1;
    if (combatLevel <= 30) return TIER_COLORS.tier2;
    if (combatLevel <= 45) return TIER_COLORS.tier3;
    if (combatLevel <= 60) return TIER_COLORS.tier4;
    if (combatLevel <= 75) return TIER_COLORS.tier5;
    return TIER_COLORS.tier6;
}

/**
 * Format a stat comparison with color coding
 * @param {number} itemValue - The item's stat value
 * @param {number} equippedValue - The equipped item's stat value
 * @param {string} statLabel - Label for the stat (e.g., "Attack")
 * @param {boolean} showComparison - Whether to show comparison
 * @param {boolean} lowerIsBetter - True for stats like attack speed where lower is better
 * @returns {string} Formatted stat line with color
 */
export function formatStatComparison(itemValue, equippedValue, statLabel, showComparison, lowerIsBetter = false) {
    const diff = itemValue - equippedValue;

    if (!showComparison || diff === 0) {
        // No comparison or same value - use neutral gray
        if (showComparison && diff === 0) {
            return `<span style='color: #888;'>+${itemValue} ${statLabel}</span>`;
        }
        return `+${itemValue} ${statLabel}`;
    }

    // Determine if this is better or worse
    const isBetter = lowerIsBetter ? diff < 0 : diff > 0;
    const color = isBetter ? '#4CAF50' : '#f44336'; // green or red
    const sign = diff > 0 ? '+' : '';

    return `<span style='color: ${color};'>+${itemValue} ${statLabel} (${sign}${diff})</span>`;
}

/**
 * Determine which equipment set an item belongs to based on its style
 * @param {Object} item - The item to check
 * @returns {string|null} 'melee', 'ranged', 'magic', or null if universal
 */
export function getItemCombatStyle(item) {
    if (!item) return null;

    // Check attackStyle for weapons
    if (item.attackStyle) {
        return item.attackStyle.toLowerCase();
    }

    // Check armorType for armor
    if (item.armorType) {
        return item.armorType.toLowerCase();
    }

    // Check for accuracy types to infer style
    if (item.meleeAccuracy && !item.rangedAccuracy && !item.magicAccuracy) return 'melee';
    if (item.rangedAccuracy && !item.meleeAccuracy && !item.magicAccuracy) return 'ranged';
    if (item.magicAccuracy && !item.meleeAccuracy && !item.rangedAccuracy) return 'magic';

    return null; // Universal item (rings, some accessories)
}

/**
 * Get the equipped item for comparison based on item style and slot
 * @param {Object} item - The item to compare
 * @returns {Object|null} The equipped item to compare against, or null
 */
export function getEquippedItemForComparison(item) {
    if (!item || !item.slot) return null;

    const itemStyle = getItemCombatStyle(item);
    const sets = gameState.getEquipmentSets();

    // Get the appropriate set's gear based on item style
    let compareSet = itemStyle || gameState.getActiveEquipmentSet();
    if (compareSet && sets[compareSet]) {
        const equippedItemId = sets[compareSet].gear[item.slot];
        if (equippedItemId) {
            return getItem(equippedItemId);
        }
    }

    return null;
}

/**
 * Build equipment stats tooltip with comparison to currently equipped item
 * @param {string} itemId - The item ID to build tooltip for
 * @param {boolean} showComparison - Whether to show comparison (default true)
 * @returns {string} HTML tooltip string
 */
export function buildEquipmentStatsTooltip(itemId, showComparison = true) {
    const item = getItem(itemId);
    if (!item) return getItemName(itemId);

    const name = item.name || itemId;
    const tierColor = getTierColor(item.combatLevel);
    const coloredName = `<span style='color: ${tierColor}; font-weight: bold;'>${name}</span>`;

    // Get equipped item for comparison
    const equippedItem = showComparison ? getEquippedItemForComparison(item) : null;
    // Compare even when slot is empty — going from nothing to something is an upgrade
    const doCompare = showComparison && item.slot;

    // Build stat list with comparisons
    const stats = [];

    // For magic weapons (staffs), show spell damage multiplier instead of attack
    if (item.magicDamage) {
        const equipped = equippedItem?.magicDamage || 0;
        const diff = item.magicDamage - equipped;
        const isBetter = diff > 0;
        const color = doCompare ? (isBetter ? '#4CAF50' : (diff < 0 ? '#f44336' : '#888')) : '';
        const diffText = doCompare && diff !== 0 ? ` (${diff > 0 ? '+' : ''}${diff.toFixed(1)})` : '';
        if (color) {
            stats.push(`<span style='color: ${color};'>${item.magicDamage.toFixed(1)}x Spell Dmg${diffText}</span>`);
        } else {
            stats.push(`${item.magicDamage.toFixed(1)}x Spell Dmg`);
        }
    } else if (item.attackBonus) {
        const equipped = equippedItem?.attackBonus || 0;
        stats.push(formatStatComparison(item.attackBonus, equipped, 'Attack', doCompare));
    }

    if (item.defenseBonus) {
        const equipped = equippedItem?.defenseBonus || 0;
        stats.push(formatStatComparison(item.defenseBonus, equipped, 'Defense', doCompare));
    }

    // Show spell damage bonus from magic armor
    if (item.spellDamage) {
        const equipped = equippedItem?.spellDamage || 0;
        const itemPct = Math.round(item.spellDamage * 100);
        const equippedPct = Math.round(equipped * 100);
        const diff = itemPct - equippedPct;
        const isBetter = diff > 0;
        const color = doCompare ? (isBetter ? '#4CAF50' : (diff < 0 ? '#f44336' : '#888')) : '';
        const diffText = doCompare && diff !== 0 ? ` (${diff > 0 ? '+' : ''}${diff}%)` : '';
        if (color) {
            stats.push(`<span style='color: ${color};'>+${itemPct}% Spell Dmg${diffText}</span>`);
        } else {
            stats.push(`+${itemPct}% Spell Dmg`);
        }
    }

    // Ranged damage bonus
    if (item.rangedDamage) {
        const equipped = equippedItem?.rangedDamage || 0;
        stats.push(formatStatComparison(item.rangedDamage, equipped, 'Ranged Dmg', doCompare));
    }

    // Accuracy stats
    if (item.meleeAccuracy) {
        const equipped = equippedItem?.meleeAccuracy || 0;
        stats.push(formatStatComparison(item.meleeAccuracy, equipped, 'Melee Acc', doCompare));
    }
    if (item.rangedAccuracy) {
        const equipped = equippedItem?.rangedAccuracy || 0;
        stats.push(formatStatComparison(item.rangedAccuracy, equipped, 'Ranged Acc', doCompare));
    }
    if (item.magicAccuracy) {
        const equipped = equippedItem?.magicAccuracy || 0;
        stats.push(formatStatComparison(item.magicAccuracy, equipped, 'Magic Acc', doCompare));
    }

    // Attack speed (lower is better)
    if (item.attackSpeed) {
        if (doCompare && equippedItem?.attackSpeed) {
            const diff = item.attackSpeed - equippedItem.attackSpeed;
            const isBetter = diff < 0; // Lower is better for speed
            const color = isBetter ? '#4CAF50' : (diff > 0 ? '#f44336' : '#888');
            const diffText = diff !== 0 ? ` (${diff > 0 ? '+' : ''}${(diff / 1000).toFixed(1)}s)` : '';
            stats.push(`<span style='color: ${color};'>${(item.attackSpeed / 1000).toFixed(1)}s Speed${diffText}</span>`);
        } else {
            stats.push(`${(item.attackSpeed / 1000).toFixed(1)}s Speed`);
        }
    }

    // Jewelry stats - speed bonus (attack speed reduction %)
    if (item.speedBonus) {
        const equipped = equippedItem?.speedBonus || 0;
        const diff = item.speedBonus - equipped;
        const isBetter = diff > 0;
        const color = doCompare ? (isBetter ? '#4CAF50' : (diff < 0 ? '#f44336' : '#888')) : '';
        const diffText = doCompare && diff !== 0 ? ` (${diff > 0 ? '+' : ''}${diff}%)` : '';
        if (color) {
            stats.push(`<span style='color: ${color};'>+${item.speedBonus}% Atk Speed${diffText}</span>`);
        } else {
            stats.push(`+${item.speedBonus}% Atk Speed`);
        }
    }

    // Crit chance bonus
    if (item.critChance) {
        const equipped = equippedItem?.critChance || 0;
        const diff = item.critChance - equipped;
        const isBetter = diff > 0;
        const color = doCompare ? (isBetter ? '#4CAF50' : (diff < 0 ? '#f44336' : '#888')) : '';
        const diffText = doCompare && diff !== 0 ? ` (${diff > 0 ? '+' : ''}${diff}%)` : '';
        if (color) {
            stats.push(`<span style='color: ${color};'>+${item.critChance}% Crit${diffText}</span>`);
        } else {
            stats.push(`+${item.critChance}% Crit`);
        }
    }

    // Generic accuracy bonus (from jewelry)
    if (item.accuracy) {
        const equipped = equippedItem?.accuracy || 0;
        stats.push(formatStatComparison(item.accuracy, equipped, 'Accuracy', doCompare));
    }

    // Bulwark stats (HP bonus and block chance)
    if (item.hpBonus) {
        const equipped = equippedItem?.hpBonus || 0;
        stats.push(formatStatComparison(item.hpBonus, equipped, 'Max HP', doCompare));
    }
    if (item.blockChance) {
        const equipped = equippedItem?.blockChance || 0;
        const diff = item.blockChance - equipped;
        const isBetter = diff > 0;
        const color = doCompare ? (isBetter ? '#4CAF50' : (diff < 0 ? '#f44336' : '#888')) : '';
        const diffText = doCompare && diff !== 0 ? ` (${diff > 0 ? '+' : ''}${diff}%)` : '';
        if (color) {
            stats.push(`<span style='color: ${color};'>${item.blockChance}% Block${diffText}</span>`);
        } else {
            stats.push(`${item.blockChance}% Block`);
        }
    }

    // Quiver stats (ammo efficiency)
    if (item.ammoEfficiency) {
        const equipped = equippedItem?.ammoEfficiency || 0;
        const diff = item.ammoEfficiency - equipped;
        const isBetter = diff > 0;
        const color = doCompare ? (isBetter ? '#4CAF50' : (diff < 0 ? '#f44336' : '#888')) : '';
        const diffText = doCompare && diff !== 0 ? ` (${diff > 0 ? '+' : ''}${diff}%)` : '';
        if (color) {
            stats.push(`<span style='color: ${color};'>${item.ammoEfficiency}% Ammo Eff${diffText}</span>`);
        } else {
            stats.push(`${item.ammoEfficiency}% Ammo Eff`);
        }
    }

    // Show equipment slot with friendly name
    if (item.slot) {
        const slotNames = {
            weapon: 'Weapon',
            offhand: 'Off-hand',
            head: 'Head',
            body: 'Body',
            legs: 'Legs',
            feet: 'Feet',
            hands: 'Hands',
            shoulders: 'Shoulders',
            neck: 'Neck',
            ring: 'Ring',
            ammo: 'Ammo'
        };
        let slotName = slotNames[item.slot] || item.slot;
        // Ranged hide armor's shoulders slot displays as Vambraces (forearm-guard art)
        if (item.slot === 'shoulders' && item.armorType === 'ranged') slotName = 'Vambraces';
        stats.push(`Slot: ${slotName}`);
    }

    if (item.attackStyle) stats.push(`Style: ${item.attackStyle}`);
    if (item.armorType) stats.push(`Type: ${item.armorType}`);
    if (item.combatLevel) stats.push(`Req: Lv ${item.combatLevel}`);
    if (item.twoHanded) stats.push('Two-Handed');

    // Show built-in ammo/spell info for starter weapons
    if (item.builtInAmmo) {
        stats.push(`Built-in: ${item.builtInAmmo.name}`);
        stats.push(`(No ammo required)`);
    }
    if (item.builtInSpell) {
        stats.push(`Built-in: ${item.builtInSpell.name}`);
        stats.push(`(No glyphs required)`);
    }

    // Show special effect description (weapons, shields, quivers)
    if (item.specialEffect?.description) {
        stats.push(`<span style='color: #FFD700;'>⚔ ${item.specialEffect.description}</span>`);
    }

    // Show unique effect description (rings, pendants)
    if (item.uniqueEffect?.description) {
        stats.push(`<span style='color: #FFD700;'>✦ ${item.uniqueEffect.description}</span>`);
    }

    // Show set armor info with full bonus details
    if (item.setId) {
        const setInfo = SET_INFO[item.setId];
        const setName = setInfo?.name || item.setId.charAt(0).toUpperCase() + item.setId.slice(1);
        const setColor = setInfo?.color || '#888';
        const state = item.awakened ? 'Awakened' : 'Dormant';
        const stateColor = item.awakened ? '#4CAF50' : '#888';

        stats.push(`<span style='color: ${setColor}; font-weight: bold;'>═══ ${setName} Set ═══</span>`);
        stats.push(`<span style='color: ${stateColor};'>Status: ${state}</span>`);

        if (!item.awakened) {
            stats.push(`<span style='color: #64b5f6; font-style: italic;'>Visit the Shop to awaken for set bonuses</span>`);
        } else {
            // Count equipped awakened pieces of this set
            const gear = gameState.getEquippedGear();
            const armorSlots = ['head', 'shoulders', 'hands', 'body', 'legs', 'feet'];
            let equippedCount = 0;
            for (const slot of armorSlots) {
                if (gear[slot]) {
                    const equipped = getItem(gear[slot]);
                    if (equipped?.setId === item.setId && equipped?.awakened) {
                        equippedCount++;
                    }
                }
            }

            stats.push(`<span style='color: #aaa;'>Equipped: ${equippedCount}/6 pieces</span>`);

            // Show set bonuses with active highlighting
            const setBonusData = SET_BONUSES[item.setId];
            if (setBonusData?.bonuses) {
                for (const threshold of [2, 4, 6]) {
                    const bonus = setBonusData.bonuses[threshold];
                    if (bonus) {
                        const isActive = equippedCount >= threshold;
                        const bonusColor = isActive ? '#4CAF50' : '#666';
                        const checkMark = isActive ? '✓ ' : '○ ';
                        stats.push(`<span style='color: ${bonusColor};'>${checkMark}(${threshold}pc) ${bonus.name}</span>`);
                        if (bonus.description) {
                            const descColor = isActive ? '#8BC34A' : '#555';
                            stats.push(`<span style='color: ${descColor}; font-size: 0.9em; padding-left: 1em;'>${bonus.description}</span>`);
                        }
                    }
                }
            }
        }
    }

    const statsText = stats.length > 0 ? `\n\n${stats.join('\n')}` : '';
    return `${coloredName}${statsText}`;
}

/**
 * Build potion effects text for tooltips
 * @param {Object} effects - Potion effects object
 * @returns {string} Formatted effects text
 */
export function buildPotionEffectsText(effects) {
    if (!effects) return '';
    const lines = [];

    const durationSec = effects.duration ? effects.duration / 1000 : 0;
    if (durationSec > 0) {
        const mins = Math.floor(durationSec / 60);
        lines.push(`Duration: ${mins}m`);
    }

    if (effects.attackBonus) lines.push(`+${effects.attackBonus} Attack`);
    if (effects.defenseBonus) lines.push(`+${effects.defenseBonus} Defense`);
    if (effects.accuracyBonus) lines.push(`+${effects.accuracyBonus}% Accuracy`);
    if (effects.speedBonus) lines.push(`+${effects.speedBonus}% Atk Speed`);
    if (effects.critBonus) lines.push(`+${effects.critBonus}% Crit Chance`);
    if (effects.meleeDamageBonus) lines.push(`+${effects.meleeDamageBonus}% Melee Dmg`);
    if (effects.rangedDamageBonus) lines.push(`+${effects.rangedDamageBonus}% Ranged Dmg`);
    if (effects.magicDamageBonus) lines.push(`+${effects.magicDamageBonus}% Magic Dmg`);
    if (effects.damageReduction) lines.push(`-${effects.damageReduction}% Damage Taken`);
    if (effects.enemyAccuracyReduction) lines.push(`-${effects.enemyAccuracyReduction}% Enemy Accuracy`);
    if (effects.glyphSaveChance) lines.push(`${effects.glyphSaveChance}% Glyph Save`);
    if (effects.ammoSaveChance) lines.push(`${effects.ammoSaveChance}% Ammo Save`);
    if (effects.corruptionBonus) lines.push(`+${effects.corruptionBonus}% Corruption`);
    if (effects.decays) lines.push(`Attack decays to +${effects.minBonus}`);

    return lines.join('\n');
}

/**
 * Calculate the effective (boosted) heal amount for food, including talent/equipment bonuses.
 * Mirrors the formula in combat.js eatFood().
 * @param {Object} item - Food item with healAmount
 * @returns {number} Effective heal amount
 */
export function getEffectiveFoodHeal(item) {
    if (!item || !item.healAmount) return 0;
    const cookingHealBonus = getTalentFlatBonus(SKILLS.COOKING, 'foodHealBonus') || 0;
    const combatFoodBonus = getTalentFlatBonus(SKILLS.COMBAT, 'foodHealing') || 0;
    // Equipment bonus (Flame Barrier ring) - check via gameState equipment
    let equipFoodBonus = 0;
    try {
        const ringId = gameState.getEquippedItem('ring');
        if (ringId) {
            const ringItem = getItem(ringId);
            if (ringItem?.uniqueEffect?.type === 'flameBarrier') {
                equipFoodBonus = ringItem.uniqueEffect.foodHealBonus || 0;
            }
        }
    } catch (e) { /* ignore if equipment lookup unavailable */ }
    return Math.floor((item.healAmount + cookingHealBonus) * (1 + combatFoodBonus + equipFoodBonus));
}

/**
 * Build food effects text for tooltips
 * @param {Object} item - Food item with healAmount and optional hot
 * @returns {string} Formatted food effects text
 */
export function buildFoodEffectsText(item) {
    if (!item || !item.healAmount) return '';
    const effectiveHeal = getEffectiveFoodHeal(item);
    let text = `Heals ${effectiveHeal} HP`;
    if (item.hot) {
        const totalHot = item.hot.amount * item.hot.ticks;
        text += ` + ${totalHot} over ${(item.hot.duration / 1000).toFixed(0)}s`;
    }
    return text;
}

/**
 * Build a general-purpose info tooltip for any item type.
 * Equipment shows stats, food shows heal, potions show effects, others just name.
 * @param {string} itemId - The item ID
 * @returns {string} HTML tooltip string
 */
export function buildItemInfoTooltip(itemId) {
    const item = getItem(itemId);
    if (!item) return getItemName(itemId);
    const name = item.name || itemId;

    // Equipment — full stat tooltip
    if (item.slot) {
        return buildEquipmentStatsTooltip(itemId);
    }

    // Potion — show effects
    if (item.effects) {
        const effectsText = buildPotionEffectsText(item.effects);
        return effectsText ? `${name}\n\n${effectsText}` : name;
    }

    // Food — show heal amount + HoT
    if (item.healAmount) {
        const foodText = buildFoodEffectsText(item);
        return foodText ? `${name}\n${foodText}` : name;
    }

    // Items with descriptions (misc items like tokens)
    if (item.description) {
        return `${name}\n\n<span style='color: #aaa; font-style: italic;'>${item.description}</span>`;
    }

    return name;
}

/**
 * Build a simple output display for crafting cards with comparison tooltip
 * @param {string} itemId - The output item ID
 * @param {number} quantity - Output quantity
 * @returns {string} HTML for the output display
 */
export function buildCraftingOutputDisplay(itemId, quantity = 1) {
    const item = getItem(itemId);
    const icon = getItemIcon(itemId);
    const tooltip = item?.slot ? buildEquipmentStatsTooltip(itemId) : getItemName(itemId);
    const qtyText = quantity > 1 ? ` x${quantity}` : '';

    return `<span class="output-item" data-tooltip="${tooltip}">${icon}${qtyText}</span>`;
}
